/////////////////////////////////////////////////////////////////////////////
// Utils

import type { CustomField } from "@situm/sdk-js";
import deepmerge from "deepmerge";

import type {
  GenericOverlayFeature,
  GeofenceOverlayFeature,
  PoiOverlayFeature,
} from "@/features/editor/types";
import type { DeepPartial } from "@/features/utils/genericity";

///////////////////////////////////////
// Translatable utils

export const getLanguagesFromCustomFields = (customFields: CustomField[]) => {
  // Always include english
  const languageSet = new Set(["en"]);

  customFields.forEach((field) => {
    // Extract code (e.g. name_es, info_fr)
    const match = field.key.match(/_(\w{2})$/);
    if (match) {
      languageSet.add(match[1]);
    }
  });

  return Array.from(languageSet);
};

const extractItemTranslatableProperty = <
  T extends PoiOverlayFeature | GeofenceOverlayFeature,
>(
  field: "name" | "info",
  properties: T["properties"],
  availableLanguagues: string[],
) => {
  const existingNames = Object.fromEntries(
    (properties.customFields || [])
      .filter((cf) => cf.key.startsWith(`${field}_`))
      .map((cf) => [cf.key.replace(`${field}_`, ""), cf.value]),
  );

  const completeNames = Object.fromEntries(
    availableLanguagues.map((lang) => [lang, existingNames[lang] ?? ""]),
  );

  return {
    ...completeNames,
    en: properties[field] ?? "",
  };
};

export const extractItemTranslatableName = <
  T extends PoiOverlayFeature | GeofenceOverlayFeature,
>(
  properties: T["properties"],
  availableLanguagues: string[],
) => {
  return extractItemTranslatableProperty(
    "name",
    properties,
    availableLanguagues,
  );
};

export const extractItemTranslatableInfo = <
  T extends PoiOverlayFeature | GeofenceOverlayFeature,
>(
  properties: T["properties"],
  availableLanguagues: string[],
) => {
  return extractItemTranslatableProperty(
    "info",
    properties,
    availableLanguagues,
  );
};

export const handleItemTranslatableContentChange = <
  T extends PoiOverlayFeature | GeofenceOverlayFeature,
>(
  updates: { field: "info" | "name"; value: { [language: string]: string } }[],
  item: T,
  onUpdate: (properties: T["properties"]) => void,
) => {
  // Create a new mutable copy of the customfields array
  const parsedCustomFields = JSON.parse(
    JSON.stringify(item.properties.customFields),
  ) as CustomField[];

  const newProperties = updates.reduce(
    (properties, { field, value }) => {
      Object.entries(value).forEach(([language, content]) => {
        if (language === "en") {
          // On english update property directly
          properties[field] = content;
        } else {
          const cfKey = `${field}_${language}`;
          // On any other language update the custom field
          const index = properties.customFields.findIndex(
            (e) => e.key === cfKey,
          );
          if (index !== -1) {
            properties.customFields[index].value = content;
          } else {
            properties.customFields.push({
              key: cfKey,
              value: content,
            });
          }
        }
      });
      return properties;
    },
    {
      customFields: parsedCustomFields,
    } as T["properties"],
  );

  onUpdate(newProperties);
};

/////////////////////////////////////////////////////////////////////////////
export function deepMergeFeatureData<T extends GenericOverlayFeature>(
  feature: T,
  newData: DeepPartial<T>,
) {
  return deepMergeKeepArrays(feature, newData);
}

/**
 * Deep merges data from "newData" into "item", completely replacing arrays
 * instead of merging them
 *
 * @param item Original item
 * @param newData New data to deepmerge and override
 */
export const deepMergeKeepArrays = <T>(item: T, newData: DeepPartial<T>) => {
  // @ts-expect-error sorry :(
  return deepmerge(item, newData, {
    arrayMerge: (_, sourceArray) => sourceArray,
  }) as T;
};
